<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  modelValue: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'destructive' | 'primary';
  confirmVariant?: 'danger';
  loading?: boolean;
  error?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Are you sure?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  confirmColor: 'destructive',
  loading: false,
  error: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  confirm: [];
  cancel: [];
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

function handleConfirm() {
  if (!props.loading) {
    emit('confirm');
  }
}

function handleCancel() {
  if (!props.loading) {
    emit('cancel');
    isOpen.value = false;
  }
}

// Generate random rotation for post-it effect
const rotation = (Math.random() - 0.5) * 3;
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-100 flex items-center justify-center p-4"
        @click="handleCancel"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/30 backdrop-blur-[2px]"></div>

        <!-- Post-it style confirmation card -->
        <div
          class="relative bg-[#fef3c7] rounded-sm shadow-2xl p-4 max-w-70 w-full text-center"
          :style="{ transform: `rotate(${rotation}deg)` }"
          @click.stop
        >
          <!-- Paper texture overlay -->
          <div
            class="absolute inset-0 rounded-sm opacity-40 pointer-events-none"
            style="
              background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E');
              mix-blend-mode: multiply;
            "
          ></div>

          <!-- Tape strip -->
          <div
            class="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-5 bg-[rgba(255,255,255,0.5)] rounded-sm shadow-sm"
            style="transform: translateX(-50%) rotate(-2deg)"
          ></div>

          <div class="relative z-10">
            <h3 class="text-base font-bold text-gray-800 mb-2">{{ title }}</h3>
            <p class="text-sm text-gray-700 mb-4">{{ message }}</p>

            <!-- Error message -->
            <div v-if="error" class="mb-4 p-2 bg-red-100 rounded text-xs text-red-700">
              {{ error }}
            </div>

            <div class="flex gap-3 justify-center">
              <button
                @click.stop="handleCancel"
                :disabled="loading"
                class="px-4 py-2 text-sm bg-white/80 hover:bg-white text-gray-700 rounded-md transition-all duration-200 shadow-sm hover:shadow cursor-pointer disabled:opacity-50"
              >
                {{ cancelText }}
              </button>
              <button
                @click.stop="handleConfirm"
                :disabled="loading"
                :class="[
                  'px-4 py-2 text-sm rounded-md transition-all duration-200 shadow-sm hover:shadow cursor-pointer disabled:opacity-50',
                  confirmColor === 'destructive' || confirmVariant === 'danger'
                    ? 'bg-(--color-destructive) hover:bg-red-600 text-white'
                    : 'bg-(--color-primary) hover:bg-opacity-90 text-white',
                ]"
              >
                {{ loading ? 'Loading...' : confirmText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
